import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import cloneDeep from 'lodash/cloneDeep';

import Fields from './Fields';
import FieldPositions from './FieldPositions';

import ThemeContext from '../../../../theme/ThemeContext';
import TabSlider from '../../../misc/TabSlider';
import FileViews from '../../../../constants/FileViews';

/**
 * Used to prop up mobile view when syncing is disabled.
 *
 * This is purely to allow a UI/UX flow so the user sees
 * our mobile view still there even if it does not exist due
 * to syncing being disabled.
 *
 * @param {Array} views with real mobile view or artifcial with desktop view
 * @returns views with artificial mobile view || views if syncing is enabled
 */
const ensureMobileView = (views = []) => {
  // Return original views if mobile already exists
  if (views.some((view) => view.type === 'mobile')) {
    return views;
  }

  // Find desktop view to clone
  const desktopView = views.find((view) => view.type === 'desktop') || views[0];

  if (!desktopView) return views;

  // Create artificial mobile view
  const artificialMobileView = {
    ...cloneDeep(desktopView),
    type: 'mobile',
    _id: 'mobile_artificial',
    clone: true
  };

  return [...views, artificialMobileView];
};

const StyledTabsWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
`;

const StyledTabs = styled.div`
  margin-top: 12px;
`;

const StyledTabWrapper = styled.div`
  opacity: 0;
  height: 0px;
  overflow: hidden;
  transition: all 0.3s ease-in-out;

  &.active {
    opacity: 1;
    height: initial;
    display: flex;
  }

`;

const STRUCTURE = 'Structure';
const FIELDS = 'New';
const tabs = [
  { id: FIELDS },
  { id: STRUCTURE },
];

const Tabs = ({
  fieldOptions,
  views,
  view,
  height,
  // artifical "desktop" view
  fields,
  onFieldSelect,
  activeFieldId,
  activeField,
  activePageId,
  onPageSelect,
  onViewSelect,
  hasMobileView
}) => {

  const defaultTab = 0;
  const [active, setActive] = useState(tabs[defaultTab].id);
  const theme = useContext(ThemeContext);

  const mergedViews = ensureMobileView(views);

  return (
    <>
      <StyledTabsWrapper>
        <StyledTabs>
          <TabSlider
            tabWidth={90}
            tabHeight={28}
            onChange={setActive}
            tabs={tabs}
            defaultTab={defaultTab}
            explicit
            tabStyles={{ fontSize: 14 }}
          />
        </StyledTabs>
      </StyledTabsWrapper>
      <StyledTabWrapper className={active === FIELDS ? 'active' : ''}>
        <Fields
          fieldOptions={fieldOptions}
          theme={theme}
          height={height}
        />
      </StyledTabWrapper>
      <StyledTabWrapper className={active === STRUCTURE ? 'active' : ''}>
        <FieldPositions
          fields={fields}
          theme={theme}
          views={mergedViews}
          view={view || FileViews.desktop}
          fieldOptions={fieldOptions}
          activeFieldId={activeFieldId}
          activeField={activeField}
          onFieldSelect={onFieldSelect}
          activePageId={activePageId}
          onPageSelect={onPageSelect}
          onViewSelect={onViewSelect}
          hasMobileView={hasMobileView}
        />
      </StyledTabWrapper>
    </>
  );
};

Tabs.propTypes = {
  fieldOptions: PropTypes.array.isRequired,
  views: PropTypes.array.isRequired,
  view: PropTypes.string,
  fields: PropTypes.array.isRequired
};

export default Tabs;
